/** useActiveTimer — current user's running TimeEntry, with start/stop actions.
 *
 * Single source of truth for the running-timer UI across the top bar and the
 * /time-entries page banner. Both subscribe to the same backend query so the
 * displayed numbers and record status stay in lock-step.
 *
 * - Polls every 10s while the tab is visible so a timer started in another
 *   tab/device appears without a manual refresh.
 * - Ticks every 1s for the elapsed display; the underlying value is derived
 *   from `entry.startTime` (server-stamped), so clock drift is bounded.
 * - Stop posts to the dedicated `/stop` endpoint; backend verifies ownership
 *   (user_id or employee_id must match the caller), so 400 means the entry
 *   is stale or owned by someone else — invalidation will refresh state.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { timeEntriesApi } from "../api";
import { useAuthStore } from "../stores/auth";
import type { TimeEntry } from "../types";

const TICK_MS = 1000;
const POLL_MS = 10_000;

export interface UseActiveTimerResult {
  entry: TimeEntry | null;
  elapsedMs: number;
  isRunning: boolean;
  isLoading: boolean;
  isStarting: boolean;
  isStopping: boolean;
  start: () => void;
  stop: () => void;
  error: string | null;
}

export function useActiveTimer(): UseActiveTimerResult {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [
      "time-entries",
      { status: "running", employeeId: currentUserId, page: 1, pageSize: 1 },
    ] as const,
    queryFn: () =>
      timeEntriesApi.list({
        employeeId: currentUserId,
        status: "running",
        page: 1,
        pageSize: 1,
      }),
    enabled: !!currentUserId,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });

  const entry = query.data?.data?.[0] ?? null;

  // 1s tick drives re-render only; elapsed is recomputed from server-stamped startTime.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!entry) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [entry?.id]);

  const elapsedMs = entry
    ? Math.max(0, now - new Date(entry.startTime).getTime())
    : 0;

  const startMutation = useMutation({
    mutationFn: () =>
      timeEntriesApi.create({
        billingStatus: "billable",
        isPrivate: false,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-entries"] });
    },
  });

  const stopMutation = useMutation({
    mutationFn: (id: string) => timeEntriesApi.stop(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-entries"] });
    },
  });

  const error =
    (startMutation.error as Error | null)?.message ??
    (stopMutation.error as Error | null)?.message ??
    null;

  return {
    entry,
    elapsedMs,
    isRunning: !!entry,
    isLoading: query.isLoading,
    isStarting: startMutation.isPending,
    isStopping: stopMutation.isPending,
    start: () => {
      if (entry || startMutation.isPending) return;
      startMutation.mutate();
    },
    stop: () => {
      if (!entry || stopMutation.isPending) return;
      stopMutation.mutate(entry.id);
    },
    error,
  };
}